import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatMoney, formatDateTime, formatPercent } from "@/lib/prefs";
import { Camera, Loader2, Trash2, TrendingUp, TrendingDown, GitCompare, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Menu Snapshots: capture a full priced menu now, compare it later.
// Used to audit margin movement across a quarter.

function CaptureDialog({ open, onOpenChange, onCaptured }) {
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/snapshots", { label: label.trim() || undefined });
      toast.success(`Snapshot captured: ${data.recipe_count} recipes, avg ${data.avg_food_cost_percent.toFixed(1)}% food cost`);
      onCaptured?.(data);
      onOpenChange(false);
      setLabel("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="snapshot-capture-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Capture menu snapshot</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Freezes every recipe's cost, sell price and food-cost % right now. Use a label like <span className="text-foreground">"End of Q1"</span> or <span className="text-foreground">"Post supplier switch"</span> so you can find it later.
          </p>
          <div>
            <Label>Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Q1 2026 baseline"
              data-testid="snapshot-label-input"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-2" data-testid="snapshot-capture-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            Capture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeltaPill({ value, invert = false }) {
  if (value == null) return <span className="text-xs text-muted-foreground">-</span>;
  const isZero = Math.abs(value) < 0.005;
  if (isZero) return <span className="text-xs text-muted-foreground tabular">±0.0</span>;
  // For food cost %, a decrease is GOOD (invert=false).
  // For margin, an increase is GOOD (invert=true).
  const good = invert ? value > 0 : value < 0;
  const Icon = value < 0 ? TrendingDown : TrendingUp;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs tabular font-semibold ${
        good ? "text-[hsl(var(--success))]" : "text-destructive"
      }`}
    >
      <Icon className="w-3 h-3" />
      {value > 0 ? "+" : ""}{value.toFixed(2)}
    </span>
  );
}

function CompareView({ compareData, onClose }) {
  const a = compareData.a;
  const b = compareData.b;
  return (
    <div className="rounded-2xl glass-card p-6 space-y-4" data-testid="snapshot-compare-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><GitCompare className="w-3 h-3" /> Comparing</div>
          <div className="font-display text-2xl mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-muted-foreground">{a.label || formatDateTime(a.created_at)}</span>
            <ArrowRight className="w-4 h-4" />
            <span>{b.label || formatDateTime(b.created_at)}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="snapshot-compare-close">Close</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl glass-card p-4">
          <div className="label-eyebrow">From avg FC%</div>
          <div className="font-display text-2xl tabular mt-1">{a.avg_food_cost_percent?.toFixed(1)}%</div>
        </div>
        <div className="rounded-xl glass-card p-4">
          <div className="label-eyebrow">To avg FC%</div>
          <div className="font-display text-2xl tabular mt-1">{b.avg_food_cost_percent?.toFixed(1)}%</div>
        </div>
        <div className="rounded-xl glass-card p-4">
          <div className="label-eyebrow">Movement</div>
          <div className="mt-2"><DeltaPill value={compareData.avg_food_cost_percent_delta} /></div>
        </div>
      </div>

      <div className="rounded-xl overflow-x-auto border border-border">
        <div className="min-w-[560px]">
        <div className="grid grid-cols-12 px-4 py-2 text-[0.6rem] uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
          <div className="col-span-5">Recipe</div>
          <div className="col-span-2 text-right">Before FC%</div>
          <div className="col-span-2 text-right">After FC%</div>
          <div className="col-span-2 text-right">Δ FC%</div>
          <div className="col-span-1 text-right">Margin Δ</div>
        </div>
        {compareData.diffs.map((d) => (
          <div
            key={d.recipe_id}
            className="grid grid-cols-12 px-4 py-2 text-sm border-b border-border/60 last:border-b-0 items-center"
            data-testid={`snapshot-diff-${d.recipe_id}`}
          >
            <div className="col-span-5 flex items-center gap-2 min-w-0">
              <span className="truncate">{d.name}</span>
              {d.status === "added" && <span className="text-[0.55rem] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-[hsl(var(--success))]">NEW</span>}
              {d.status === "removed" && <span className="text-[0.55rem] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">GONE</span>}
            </div>
            <div className="col-span-2 text-right tabular text-muted-foreground">{d.food_cost_percent_a != null ? d.food_cost_percent_a.toFixed(1) + "%" : "-"}</div>
            <div className="col-span-2 text-right tabular">{d.food_cost_percent_b != null ? d.food_cost_percent_b.toFixed(1) + "%" : "-"}</div>
            <div className="col-span-2 text-right"><DeltaPill value={d.food_cost_percent_delta} /></div>
            <div className="col-span-1 text-right"><DeltaPill value={d.margin_delta} invert /></div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

export default function Snapshots() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [compareIds, setCompareIds] = useState({ a: "", b: "" });
  const [compareData, setCompareData] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/snapshots");
      setSnapshots(data);
      // Auto-populate the compare picker with the two most recent snapshots.
      if (data.length >= 2 && !compareIds.a && !compareIds.b) {
        setCompareIds({ a: data[1].id, b: data[0].id });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const expand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    setExpandedDetail(null);
    try {
      const { data } = await api.get(`/snapshots/${id}`);
      setExpandedDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this snapshot? This is permanent.")) return;
    try {
      await api.delete(`/snapshots/${id}`);
      toast.success("Snapshot deleted");
      setExpandedId(null);
      setExpandedDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const runCompare = async () => {
    if (!compareIds.a || !compareIds.b || compareIds.a === compareIds.b) {
      toast.error("Pick two different snapshots to compare.");
      return;
    }
    setComparing(true);
    try {
      const { data } = await api.get(`/snapshots/${compareIds.a}/compare/${compareIds.b}`);
      setCompareData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setComparing(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const empty = snapshots.length === 0;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="snapshots-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Camera className="w-3 h-3" /> Menu snapshots</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Prove your margin.</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Capture your priced menu today, and again in a quarter. The compare view shows exactly which dishes moved and by how much.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2" data-testid="snapshot-capture-btn">
          <Camera className="w-4 h-4" /> Capture snapshot
        </Button>
      </header>

      {empty && (
        <div className="rounded-2xl glass-card p-10 text-center">
          <Sparkles className="w-6 h-6 gold mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No snapshots yet.</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Capture your first one now to lock in today's numbers as a baseline. In a month or a quarter, you'll be able to compare against it and see every dish that improved.
          </p>
        </div>
      )}

      {snapshots.length >= 2 && (
        <div className="rounded-2xl glass-card p-5 space-y-3" data-testid="snapshot-compare-picker">
          <div className="flex items-center gap-2 label-eyebrow"><GitCompare className="w-3 h-3" /> Compare</div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={compareIds.a} onValueChange={(v) => setCompareIds({ ...compareIds, a: v })}>
              <SelectTrigger className="w-72" data-testid="snapshot-compare-a"><SelectValue placeholder="From…" /></SelectTrigger>
              <SelectContent>
                {snapshots.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label || formatDateTime(s.created_at)} • {s.avg_food_cost_percent?.toFixed(1)}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
            <Select value={compareIds.b} onValueChange={(v) => setCompareIds({ ...compareIds, b: v })}>
              <SelectTrigger className="w-72" data-testid="snapshot-compare-b"><SelectValue placeholder="To…" /></SelectTrigger>
              <SelectContent>
                {snapshots.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label || formatDateTime(s.created_at)} • {s.avg_food_cost_percent?.toFixed(1)}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={runCompare} disabled={comparing} className="gap-2" data-testid="snapshot-compare-run">
              {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
              Show deltas
            </Button>
          </div>
        </div>
      )}

      {compareData && (
        <CompareView compareData={compareData} onClose={() => setCompareData(null)} />
      )}

      {!empty && (
        <div className="rounded-2xl glass-card overflow-x-auto" data-testid="snapshot-list">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-12 px-5 py-3 text-[0.6rem] uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
            <div className="col-span-4">Captured</div>
            <div className="col-span-2 text-right">Recipes</div>
            <div className="col-span-3 text-right">Avg food cost %</div>
            <div className="col-span-2 text-right">Batch total</div>
            <div className="col-span-1"></div>
          </div>
          {snapshots.map((s) => {
            const isExpanded = expandedId === s.id;
            return (
              <div key={s.id} className="border-b border-border/60 last:border-b-0">
                <button
                  onClick={() => expand(s.id)}
                  className="w-full grid grid-cols-12 px-5 py-4 text-sm items-center hover:bg-secondary/30 transition-colors text-left"
                  data-testid={`snapshot-row-${s.id}`}
                >
                  <div className="col-span-4">
                    <div className="font-medium">{s.label || "Untitled snapshot"}</div>
                    <div className="text-[0.7rem] text-muted-foreground mt-0.5">{formatDateTime(s.created_at)}</div>
                  </div>
                  <div className="col-span-2 text-right tabular">{s.recipe_count}</div>
                  <div className="col-span-3 text-right">
                    <span
                      className={`font-display text-lg tabular ${
                        s.avg_food_cost_percent > 35 ? "text-destructive" : "text-[hsl(var(--success))]"
                      }`}
                    >
                      {s.avg_food_cost_percent?.toFixed(1)}%
                    </span>
                  </div>
                  <div className="col-span-2 text-right tabular text-muted-foreground">
                    {formatMoney(s.total_batch_cost || 0)}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      onClick={(e) => remove(s.id, e)}
                      className="text-muted-foreground/60 hover:text-destructive transition-colors p-1"
                      data-testid={`snapshot-delete-${s.id}`}
                      aria-label="Delete snapshot"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5 pt-1" data-testid={`snapshot-detail-${s.id}`}>
                    {!expandedDetail ? (
                      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                    ) : (
                      <div className="rounded-xl overflow-hidden border border-border/60">
                        <div className="grid grid-cols-12 px-4 py-2 text-[0.6rem] uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/20">
                          <div className="col-span-5">Recipe</div>
                          <div className="col-span-2 text-right">Sell</div>
                          <div className="col-span-2 text-right">Cost / serving</div>
                          <div className="col-span-2 text-right">FC %</div>
                          <div className="col-span-1 text-right">Margin</div>
                        </div>
                        {expandedDetail.recipes.map((r) => (
                          <div key={r.recipe_id} className="grid grid-cols-12 px-4 py-2 text-sm border-b border-border/40 last:border-b-0 items-center">
                            <div className="col-span-5 truncate">{r.name}</div>
                            <div className="col-span-2 text-right tabular text-muted-foreground">{formatMoney(r.sell_price)}</div>
                            <div className="col-span-2 text-right tabular">{formatMoney(r.cost_per_serving)}</div>
                            <div className={`col-span-2 text-right tabular font-semibold ${
                              r.sell_price > 0
                                ? r.food_cost_percent > 35 ? "text-destructive" : "text-[hsl(var(--success))]"
                                : "text-muted-foreground"
                            }`}>
                              {r.sell_price > 0 ? formatPercent(r.food_cost_percent, 1) : "-"}
                            </div>
                            <div className="col-span-1 text-right tabular text-muted-foreground">{formatMoney(r.margin_per_serving)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}

      <CaptureDialog open={dialogOpen} onOpenChange={setDialogOpen} onCaptured={load} />
    </div>
  );
}
